import { useState } from "react";
import { ChevronDown, ChevronRight, Gauge, Calculator, Zap, TrendingUp, Cpu, Database, Filter, Calendar } from "lucide-react";

const SFOCEfficiency = () => {
  const [openSections, setOpenSections] = useState(false);

  const toggleSection = () => {
    setOpenSections((prev) => !prev);
  };

  const calculations = [
    {
      id: "power-percentage",
      title: "Power Load Percentage",
      description: "Actual power output as percentage of Maximum Continuous Rating",
      formula: "(totalMePower ÷ totalMcrPower) × 100",
      code: "const powerPercentage = (totalMePower / totalMcrPower) * 100;",
      fields: ["ME Power (KW)", "ME MCR (KW)"],
      output: "Percentage (%)",
      borderColor: "border-blue-200",
      bgColor: "bg-blue-50",
      textColor: "text-blue-700",
      icon: <Zap className="w-5 h-5" />,
    },
    {
      id: "sfoc",
      title: "SFOC Performance",
      description: "Specific Fuel Oil Consumption - fuel efficiency metric",
      formula: "(mainEngineFuelConsumption × 1,000,000) ÷ (totalMePower × (fuelNCV ÷ 42.707))",
      code: `const sfocPerformance = (totalMePower > 0 && fuelNCV > 0)
  ? (mainEngineFuelConsumption * 1000000) / (totalMePower * (fuelNCV / 42.707))
  : 0;`,
      fields: ["Fuel_NCV_MJ_kg", "ME Power (KW)", "Main Engine Fuel Consumption"],
      output: "g/kWh (grams per kilowatt-hour)",
      borderColor: "border-green-200",
      bgColor: "bg-green-50",
      textColor: "text-green-700",
      icon: <Gauge className="w-5 h-5" />,
    },
  ];

  return (
    <div>
      <div className="mb-6 border border-gray-300 rounded-lg overflow-hidden">
        <button
          className="flex items-center justify-between w-full p-5 bg-gray-50 hover:bg-gray-100 transition-colors"
          onClick={toggleSection}
        >
          <div className="flex items-center gap-4">
            <div className="p-3 bg-[#fce3d2] rounded-lg">
              <Gauge className="w-6 h-6 text-[#d35400]" />
            </div>
            <div className="text-left">
              <h2 className="text-md font-bold text-gray-800">
                SFOC & Power Efficiency Analysis
              </h2>
              <p className="text-gray-600 text-sm">
                SFOC performance and power load percentage calculations
              </p>
            </div>
          </div>
          {openSections ? (
            <ChevronDown className="w-5 h-5 text-gray-500" />
          ) : (
            <ChevronRight className="w-5 h-5 text-gray-500" />
          )}
        </button>

        {openSections && (
          <div className="p-5 bg-white border-t border-gray-200">
            {/* Calculation Formulas */}
            <div className="mb-6">
              <h4 className="font-semibold text-gray-800 mb-3">Performance Calculations</h4>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                {calculations.map((calc) => (
                  <div
                    key={calc.id}
                    className={`p-5 rounded-lg border ${calc.borderColor} ${calc.bgColor}`}
                  >
                    <div className="flex items-center gap-3 mb-4">
                      <div className="p-2 rounded-lg bg-white border">
                        {calc.icon}
                      </div>
                      <div className="flex-1">
                        <div className="flex justify-between items-start">
                          <h4 className="font-semibold text-gray-800">
                            {calc.title}
                          </h4>
                          <span className={`text-xs font-medium px-2 py-1 rounded ${calc.textColor} ${calc.bgColor.replace('50', '100')}`}>
                            {calc.output}
                          </span>
                        </div>
                      </div>
                    </div>

                    <div className="mb-3">
                      <p className="text-sm font-medium text-gray-700 mb-1">Description:</p>
                      <p className="text-sm text-gray-600">{calc.description}</p>
                    </div>

                    <div className="mb-3">
                      <p className="text-sm font-medium text-gray-700 mb-1">Mathematical Formula:</p>
                      <div className="text-sm text-gray-700 bg-white p-2 rounded border font-mono">
                        {calc.formula}
                      </div>
                    </div>

                    <div className="mb-3">
                      <p className="text-sm font-medium text-gray-700 mb-1">Implementation:</p>
                      <div className="text-xs text-gray-700 bg-white p-2 rounded border font-mono overflow-x-auto">
                        {calc.code}
                      </div>
                    </div>

                    <div className="mb-3">
                      <p className="text-sm font-medium text-gray-700 mb-1">Source Data Fields:</p>
                      <div className="flex flex-wrap gap-1">
                        {calc.fields.map((field) => (
                          <code key={field} className="text-xs bg-gray-100 p-1 rounded">
                            {field}
                          </code>
                        ))}
                      </div>
                    </div>

                    <div>
                      <p className="text-sm font-medium text-gray-700 mb-1">Calculation Logic:</p>
                      <ul className="text-sm text-gray-600 space-y-1">
                        {calc.id === "sfoc" && (
                          <>
                            <li className="flex items-start gap-1">
                              <span className="text-xs">•</span>
                              <span><strong>Condition Check:</strong> Requires totalMePower &gt; 0 and fuelNCV &gt; 0</span>
                            </li>
                            <li className="flex items-start gap-1">
                              <span className="text-xs">•</span>
                              <span><strong>Unit Conversion:</strong> 42.707 converts MJ/kg to MJ/g for g/kWh result</span>
                            </li>
                            <li className="flex items-start gap-1">
                              <span className="text-xs">•</span>
                              <span><strong>Scalar:</strong> ×1,000,000 converts kg to g</span>
                            </li>
                          </>
                        )}
                        {calc.id === "power-percentage" && (
                          <>
                            <li className="flex items-start gap-1">
                              <span className="text-xs">•</span>
                              <span><strong>Power Ratio:</strong> Actual power ÷ Maximum Continuous Rating</span>
                            </li>
                            <li className="flex items-start gap-1">
                              <span className="text-xs">•</span>
                              <span><strong>Result:</strong> Shows engine load as percentage of capacity</span>
                            </li>
                          </>
                        )}
                      </ul>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default SFOCEfficiency;